import { useId } from 'react';
import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { Link } from 'react-router-dom';
import { Modal } from 'reactstrap';
import cx from 'classnames';

interface ActionSheetProps {
  isOpen: boolean;
  toggle: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Buttons pinned under the list, e.g. a confirm/cancel pair. */
  footer?: ReactNode;
  onClosed?: () => void;
}

/**
 * A short list of choices: a sheet that slides up from the bottom on a phone,
 * where the thumb already is, and a small centred dialog on wider screens.
 *
 * Built on reactstrap's Modal for the focus trap, Escape and scroll lock; the
 * `v-sheet` class is what turns it into a sheet below 576px.
 */
export default function ActionSheet({
  isOpen,
  toggle,
  title,
  subtitle,
  children,
  footer,
  onClosed,
}: ActionSheetProps) {
  const titleId = useId();

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="sm"
      centered
      modalClassName="v-sheet"
      labelledBy={title ? titleId : undefined}
      onClosed={onClosed}
    >
      {title && (
        <div className="v-sheet__header">
          <div>
            <h2 className="v-sheet__title" id={titleId}>
              {title}
            </h2>
            {subtitle && <p className="v-sheet__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="btn-close" aria-label="Cerrar" onClick={toggle} />
        </div>
      )}
      <div className="v-sheet__body">{children}</div>
      {footer && <div className="v-sheet__actions">{footer}</div>}
    </Modal>
  );
}

interface ActionSheetItemProps {
  icon?: IconType;
  label: ReactNode;
  hint?: ReactNode;
  /** Right-aligned detail: an amount, a count. */
  trailing?: ReactNode;
  /** A route to go to; otherwise the item is a button. */
  to?: string;
  onClick?: () => void;
  danger?: boolean;
  active?: boolean;
  disabled?: boolean;
}

/** One row of an ActionSheet: icon, label and an optional one-line hint. */
export function ActionSheetItem({
  icon: Icon,
  label,
  hint,
  trailing,
  to,
  onClick,
  danger,
  active,
  disabled,
}: ActionSheetItemProps) {
  const className = cx('v-sheet__item', {
    'v-sheet__item--danger': danger,
    active,
  });

  const inner = (
    <>
      {Icon && (
        <span className="v-sheet__icon" aria-hidden="true">
          <Icon />
        </span>
      )}
      <span className="v-sheet__item-text">
        <span>{label}</span>
        {hint && <span className="v-sheet__item-hint">{hint}</span>}
      </span>
      {trailing && <span className="v-sheet__trailing">{trailing}</span>}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={className}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled}>
      {inner}
    </button>
  );
}

export function ActionSheetDivider() {
  return <div className="v-sheet__divider" role="separator" />;
}
